/**
 * `/flights` — the pilot's logbook of intentions: what am I flying, and when.
 *
 * Flights are grouped by month, newest first; templates sit above them under
 * their own eyebrow because a template is a different kind of thing (a route
 * you fly often, with no date of its own). The row's `⋯` menu carries the four
 * gestures that are otherwise a copy-paste job — above all *Rückflug anlegen*,
 * which is the most common thing a GA pilot does with a plan they already have.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../api';
import { AppShell } from '../app/AppShell';
import { EmptyState } from '../app/EmptyState';
import { FlightRow, type FlightAction } from '../app/FlightRow';
import { PageHeader } from '../app/PageHeader';
import { dateToIso, newFlightStart } from '../fields';
import { useT, type T } from '../i18n';
import { useSession } from '../session';

/** `AUGUST 2026` — the eyebrow a group of flights sits under. */
function monthLabel(datum: string | null, locale: string, fallback: string): string {
  const iso = datum ? dateToIso(datum) : '';
  if (!iso) return fallback;
  const d = new Date(iso + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** Sort key: dated flights descending, undated last. */
const sortKey = (p: api.PlanSummary): string => dateToIso(p.datum ?? '') || '0000-00-00';

interface Group {
  key: string;
  label: string;
  plans: api.PlanSummary[];
}

function groupByMonth(plans: readonly api.PlanSummary[], t: T): Group[] {
  const out = new Map<string, Group>();
  for (const p of [...plans].sort((a, b) => sortKey(b).localeCompare(sortKey(a)))) {
    const iso = dateToIso(p.datum ?? '');
    const key = iso ? iso.slice(0, 7) : '';
    const label = monthLabel(p.datum, t('locale'), t('flights.undated'));
    const g = out.get(key) ?? { key, label, plans: [] };
    g.plans.push(p);
    out.set(key, g);
  }
  return [...out.values()];
}

export default function Flights() {
  const session = useSession();
  const navigate = useNavigate();
  const t = useT();
  const [plans, setPlans] = useState<api.PlanSummary[] | null>(null);
  const [aircraft, setAircraft] = useState<api.Aircraft[]>([]);
  // the raw error is kept, not its text: switching language re-renders the message
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([api.listPlans(), api.listAircraft()]);
      setPlans(p);
      setAircraft(a);
      setError(null);
    } catch (e) {
      if (e instanceof api.ApiError && e.unauthenticated) {
        navigate('/login', { replace: true });
        return;
      }
      setPlans([]);
      setError(e);
    }
  }, [navigate]);

  useEffect(() => {
    if (session.loading) return;
    if (!session.user) {
      navigate('/login', { replace: true });
      return;
    }
    void reload();
  }, [session.loading, session.user, navigate, reload]);

  const create = async (isTemplate: boolean) => {
    setBusy(true);
    try {
      // A new FLIGHT starts now; a template has no date of its own and must not
      // be given one (see `newFlightStart`).
      const start = isTemplate ? null : newFlightStart(new Date());
      const p = await api.createPlan({
        title: isTemplate ? t('flights.newTemplate') : t('flights.new'),
        is_template: isTemplate,
        values: start ? { datum: start.datum, etd: start.etd } : {},
        manual: start ? ['datum', 'etd'] : [],
      });
      navigate('/flights/' + p.id);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  /** Copy a plan, optionally reversed and/or as a template. */
  const copy = async (id: string, opts: { reverse?: boolean; template?: boolean; title?: string }) => {
    setBusy(true);
    try {
      const src = await api.getPlan(id);
      const waypoints = src.route ? [...src.route.waypoints] : [];
      if (opts.reverse) waypoints.reverse();
      const values = { ...src.values };
      // a copy is a different flight: it keeps the route and the aircraft, never
      // the times of the one it came from
      if (opts.reverse || opts.template) {
        delete values['datum'];
        delete values['etd'];
        delete values['atd'];
        delete values['eta'];
        delete values['ata'];
      }
      const p = await api.createPlan({
        title: opts.title ?? src.title,
        is_template: opts.template === true,
        aircraft_id: src.aircraft_id,
        values,
        manual: src.manual.filter(k => values[k] !== undefined),
        route: src.route ? { ...src.route, waypoints } : null,
        meta: src.meta,
      });
      navigate('/flights/' + p.id);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const instantiate = async (id: string) => {
    setBusy(true);
    try {
      const p = await api.instantiatePlan(id);
      navigate('/flights/' + p.id);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const rename = async (p: api.PlanSummary) => {
    const title = window.prompt(t('flights.rename.prompt'), p.title);
    if (title === null || title === p.title) return;
    try {
      const full = await api.getPlan(p.id);
      const res = await api.updatePlan(p.id, {
        title,
        is_template: full.is_template,
        aircraft_id: full.aircraft_id,
        values: full.values,
        manual: full.manual,
        route: full.route,
        meta: full.meta,
        version: full.version,
      });
      if (res.ok) await reload();
    } catch (e) {
      setError(e);
    }
  };

  const remove = async (p: api.PlanSummary) => {
    if (!window.confirm(t('flights.confirm.delete', { title: p.title || t('flights.untitled') }))) return;
    try {
      await api.deletePlan(p.id);
      await reload();
    } catch (e) {
      setError(e);
    }
  };

  const regOf = (id: string | null): string | undefined =>
    aircraft.find(a => a.id === id)?.registration;

  const actionsFor = (p: api.PlanSummary): FlightAction[] => {
    const list: FlightAction[] = [
      { key: 'open', label: t('flights.open'), run: () => navigate('/flights/' + p.id) },
    ];
    if (p.is_template) {
      list.push({ key: 'inst', label: t('flights.instantiate'), run: () => void instantiate(p.id) });
    } else {
      list.push(
        { key: 'return', label: t('flights.return'), run: () => void copy(p.id, { reverse: true }) },
        { key: 'dup', label: t('flights.duplicate'), run: () => void copy(p.id, {}) },
        { key: 'tpl', label: t('flights.saveTemplate'), run: () => void copy(p.id, { template: true }) },
      );
    }
    list.push(
      { key: 'rename', label: t('flights.rename'), run: () => void rename(p) },
      { key: 'del', label: t('flights.delete'), danger: true, run: () => void remove(p) },
    );
    return list;
  };

  const todayIso = new Date().toISOString().slice(0, 10);
  const flights = (plans ?? []).filter(p => !p.is_template);
  const templates = (plans ?? []).filter(p => p.is_template);
  const groups = useMemo(() => groupByMonth(flights, t), [plans, t]);

  const row = (p: api.PlanSummary) => (
    <FlightRow
      key={p.id}
      plan={p}
      registration={regOf(p.aircraft_id)}
      actions={actionsFor(p)}
      todayIso={todayIso}
    />
  );

  return (
    <AppShell active="flights">
      <div className="ae-page">
        <PageHeader
          title={t('flights.title')}
          actions={
            <>
              <button
                type="button"
                className="ae-btn ae-btn-primary"
                disabled={busy}
                onClick={() => void create(false)}
              >
                {t('flights.new')}
              </button>
              <button type="button" className="ae-btn" disabled={busy} onClick={() => void create(true)}>
                {t('flights.newTemplate')}
              </button>
            </>
          }
        />

        {error ? (
          <div className="ae-banner warning">
            <span className="ae-banner-text">{t('flights.error')}</span>
            <button type="button" className="ae-btn" onClick={() => void reload()}>
              {t('flights.retry')}
            </button>
          </div>
        ) : null}

        {plans === null ? (
          <div className="ae-skeleton" aria-hidden="true">
            <i />
            <i />
            <i />
          </div>
        ) : null}

        {templates.length ? (
          <section className="ae-group">
            <h2 className="ae-eyebrow">{t('flights.templates')}</h2>
            {templates.map(row)}
          </section>
        ) : null}

        {groups.map(g => (
          <section className="ae-group" key={g.key}>
            <h2 className="ae-eyebrow">{g.label}</h2>
            {g.plans.map(row)}
          </section>
        ))}

        {plans !== null && flights.length === 0 && templates.length === 0 ? (
          <EmptyState
            title={t('flights.empty.title')}
            line={t('flights.empty.line')}
            action={
              <button
                type="button"
                className="ae-btn ae-btn-primary"
                disabled={busy}
                onClick={() => void create(false)}
              >
                {t('flights.new')}
              </button>
            }
          />
        ) : null}
      </div>
    </AppShell>
  );
}
